import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { CloseRounded, PictureAsPdfRounded } from '@mui/icons-material';

// Full-screen-ish viewer for a server-rendered report page, with a PDF download button
export default function ReportViewerDialog({
  open,
  onClose,
  title,
  subtitle,
  headerExtra = null,
  banner = null,
  html,
  loading,
  error,
  onRetry,
  onDownload,
  downloading,
  downloadDisabled = false,
}) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{ sx: { borderRadius: '16px', height: '92vh' } }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        spacing={1.5}
        sx={{ px: 3, py: 1.75, borderBottom: '1px solid', borderColor: 'divider' }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={700}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" flexShrink={0}>
          {headerExtra}
          <Button
            size="small"
            variant="contained"
            disableElevation
            startIcon={
              downloading ? <CircularProgress size={14} color="inherit" /> : <PictureAsPdfRounded />
            }
            disabled={downloading || downloadDisabled}
            onClick={onDownload}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '8px' }}
          >
            {downloading ? 'Preparing PDF…' : 'Download PDF'}
          </Button>
          <IconButton onClick={onClose} aria-label="Close report">
            <CloseRounded />
          </IconButton>
        </Stack>
      </Stack>
      {banner}
      <DialogContent sx={{ p: 0, bgcolor: '#f4f6f8', display: 'flex' }}>
        {loading ? (
          <Stack alignItems="center" justifyContent="center" spacing={1.5} sx={{ flex: 1 }}>
            <CircularProgress size={30} />
            <Typography variant="body2" color="text.secondary">
              Loading report…
            </Typography>
          </Stack>
        ) : error ? (
          <Box sx={{ flex: 1, p: 3 }}>
            <Alert
              severity="info"
              action={
                <Button color="inherit" size="small" onClick={onRetry}>
                  Retry
                </Button>
              }
            >
              {error}
            </Alert>
          </Box>
        ) : (
          <Box
            component="iframe"
            title={title || 'Report'}
            // No allow-scripts: the server's report page is static HTML
            sandbox=""
            srcDoc={html}
            sx={{ flex: 1, width: '100%', border: 0, bgcolor: '#fff' }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
